// Zentrale Design-Tokens fuer ComeFit.
//
// Ziel: Farben, Abstaende, Schriftgroessen, Radien und Komponenten-Groessen an
// EINER Stelle definieren, statt als literale Werte ("#2b8cc4", 16, "1rem", ...)
// ueber die ganze App verstreut. Aendert man hier einen Wert, wirkt sich das
// ueberall aus, wo der jeweilige Token verwendet wird.
//
// Das ist die Grundlage fuer ein spaeteres Theme-System (z.B. "Einstellungen ->
// Design"): dieses Objekt koennte kuenftig dynamisch aus Server-Einstellungen
// befuellt werden (z.B. mehrere benannte Themes wie "ComeFit Standard",
// "Modern", "Hell", kundenspezifisch), ohne dass Komponenten-Code angefasst
// werden muss - Komponenten lesen ausschliesslich aus THEME.*.
//
// WICHTIG: Alle Werte hier entsprechen exakt dem, was vorher als Literal im
// Code stand - diese Datei aendert (noch) nichts an der Optik, sie macht die
// bestehenden Werte nur benennbar und zentral aenderbar.

const THEME = {
  colors: {
    // Marke / Primaerfarbe - entspricht dem "Modern Dunkel"-Preset (App.js DESIGN_PRESETS),
    // das jetzt der tatsaechliche Fallback ist, solange noch keine gespeicherten
    // Design-Einstellungen vom Server geladen wurden (z.B. direkt nach "Cache leeren").
    primary: "#6366f1",
    accent: "#f59e0b",
    // Eigene, von "primary" unabhaengige Farbe fuer die aktive Mitglieder-Tab-Reihe (Mitglieder/
    // Wellpass-Nutzer/Hansefit-Nutzer/Sonstige) - damit sich dieser eine aktive Reiter separat
    // einfaerben laesst, ohne die Primaerfarbe der restlichen App mitzuziehen. Der inaktive
    // Zustand nutzt bewusst denselben Token wie jeder andere graue Button/Tab app-weit
    // (surfaceMuted, siehe unten) statt eines eigenen "tabInaktiv"-Tokens.
    tabAktiv: "#6366f1",

    // Flaechen
    background: "#0a0a0f",   // Seiten-/App-Hintergrund
    surface: "#18181f",      // Karten, Sidebar, Modals, Login-Karte
    surfaceMuted: "#2b3d52", // Sekundaere Flaechen (btnGray, inaktive Tab-Reiter app-weit, aktiver Nav-Punkt, Trennlinien, Hover)
    border: "#27272f",       // Rahmenfarbe (aktuell gleicher Wert wie hover, eigener Token fuer spaetere Trennung)
    hover: "#27272f",        // Hover-Hintergrund (aktuell gleicher Wert, eigener Token fuer spaetere Trennung)

    // Text
    text: "#f4f4f5",
    textMuted: "#9ca3af",
    textSecondary: "#aabbcc",
    textDisabled: "#6b7280",

    // Status
    success: "#22c55e",
    warning: "#f59e0b",
    danger: "#ef4444",
    dangerBg: "#7f1d1d",

    // Zusaetzliche Akzentfarben (Kategorie-/Modul-Badges: Partner, Dokumente, Aktionen, ...)
    accentPurple: "#a78bfa",
    accentViolet: "#7c3aed",
    accentPink: "#db2777",
    accentCyan: "#0891b2",
    accentOrange: "#f97316",
    accentGreenDark: "#16a34a",
  },

  radius: {
    sm: 8,
    md: 10,
    lg: 14,
    xl: 16,
    xxl: 18,
    xxxl: 20,
  },

  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
    xxxl: 28,
  },

  fontSize: {
    tiny: 10,
    xxs: 11,
    xs: 12,
    sm: 13,
    base: 14,
    md: 15,
    lg: 16,
    lg2: 17,
    xl: 18,
    xl2: 20,
    xxl: 22,
    xxl2: 24,
    xxxl: 26,
    xxxl2: 28,
    heading: 30,
    display: 32,
    hero: 60,
  },

  shadow: {
    none: "none", // aktuelles dunkles Theme arbeitet mit Rahmen/Flaechen statt Schatten
  },

  sizes: {
    sidebarWidth: 252,
    sidebarCollapsedWidth: 78,
    loginCardMaxWidth: 420,
    modalMaxWidth: "min(820px, 95vw)",
  },
};

// ---------------------------------------------------------------------------
// Laufzeit-Overrides fuer "Einstellungen -> Design"
//
// Die Design-Seite speichert Farben serverseitig unter Settings-Keys wie
// "design_primary". DESIGN_KEY_MAP bildet diese Keys auf THEME.colors.* ab.
// Da `THEME.colors` von App.js beim Modul-Start synchron in eigene
// Style-Objekte (das `s = {...}`) kopiert wird, muessen Overrides VOR diesem
// Zeitpunkt vorliegen - dafuer dient der localStorage-Cache: er wird sofort
// beim Laden dieses Moduls angewendet (synchron, kein Netzwerk-Warten noetig).
// Ein Server-Fetch (siehe EinstellungenSeite) haelt den Cache aktuell und
// wendet neue Werte per Reload an.
export const DESIGN_KEY_MAP = {
  design_primary: "primary",
  design_accent: "accent",
  design_background: "background",
  design_surface: "surface",
  design_text: "text",
  design_textMuted: "textMuted",
  design_border: "border",
  design_hover: "hover",
  design_success: "success",
  design_warning: "warning",
  design_danger: "danger",
  design_tabAktiv: "tabAktiv",
  design_tabInaktiv: "surfaceMuted",
};

// Berechnet automatisch eine gut lesbare Text-/Icon-Farbe fuer einen beliebig gewaehlten
// Hintergrund (z.B. die frei waehlbare Tab-Aktiv-Farbe) - verhindert, dass eine helle
// Hintergrundfarbe zusammen mit hellem Text unlesbar wird. Einfache Wahrnehmungs-Helligkeit
// (kein volles WCAG-Kontrastverhaeltnis noetig, reicht fuer diesen Zweck).
export function kontrastTextfarbe(hexFarbe, hellesText = "#ffffff", dunklesText = "#111827") {
  const hex = (hexFarbe || "").replace("#", "");
  if (hex.length !== 6) return hellesText;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const helligkeit = (r * 299 + g * 587 + b * 114) / 1000;
  return helligkeit > 150 ? dunklesText : hellesText;
}

const OVERRIDE_STORAGE_KEY = "comefit_design_overrides";

function ladeGespeicherteOverrides() {
  try {
    const raw = localStorage.getItem(OVERRIDE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const _gespeicherteOverrides = ladeGespeicherteOverrides();
if (_gespeicherteOverrides) {
  Object.assign(THEME.colors, _gespeicherteOverrides);
}

// Nimmt das rohe Einstellungen-Dict (wie von GET /einstellungen/ geliefert),
// wendet die enthaltenen design_*-Werte sofort auf THEME.colors an und
// merkt sie sich im localStorage fuer den naechsten Seitenaufruf.
export function designUebernehmenAusEinstellungen(einstellungenDict) {
  const overrides = {};
  for (const [settingKey, themeKey] of Object.entries(DESIGN_KEY_MAP)) {
    if (einstellungenDict[settingKey]) overrides[themeKey] = einstellungenDict[settingKey];
  }
  Object.assign(THEME.colors, overrides);
  try {
    localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify({ ...(ladeGespeicherteOverrides() || {}), ...overrides }));
  } catch {}
  return overrides;
}

export function designUeberschreibungenZuruecksetzen() {
  try {
    localStorage.removeItem(OVERRIDE_STORAGE_KEY);
  } catch {}
}

// ---------------------------------------------------------------------------
// Schriftgroesse (Einstellungen -> Design): 4 feste Stufen statt freier Eingabe.
//
// BASE_FONT_SIZES ist eine Kopie der urspruenglichen (unskalierten) Werte aus
// THEME.fontSize - jede Stufe multipliziert diese Basis, THEME.fontSize wird
// darauf ueberschrieben. Gleiches localStorage-Cache-Pattern wie bei Farben:
// wird beim Modul-Laden angewendet, bevor App.js das `s`-Objekt definiert.
const BASE_FONT_SIZES = { ...THEME.fontSize };

export const FONT_SCALE_OPTIONS = {
  klein: 1.15,
  standard: 1.3,
  gross: 1.45,
};

const FONT_SCALE_STORAGE_KEY = "comefit_font_scale";

function ladeGespeicherteSchriftgroesse() {
  try {
    return localStorage.getItem(FONT_SCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function wendeSchriftgroesseAn(stufe) {
  const faktor = FONT_SCALE_OPTIONS[stufe] ?? 1.0;
  const skaliert = {};
  for (const [key, basis] of Object.entries(BASE_FONT_SIZES)) {
    skaliert[key] = Math.round(basis * faktor);
  }
  Object.assign(THEME.fontSize, skaliert);
}

const _gespeicherteSchriftgroesse = ladeGespeicherteSchriftgroesse();
if (_gespeicherteSchriftgroesse) {
  wendeSchriftgroesseAn(_gespeicherteSchriftgroesse);
}

// Nimmt das rohe Einstellungen-Dict, wendet "design_schriftgroesse" sofort auf
// THEME.fontSize an und merkt sich die Stufe im localStorage.
export function schriftgroesseUebernehmenAusEinstellungen(einstellungenDict) {
  const stufe = einstellungenDict.design_schriftgroesse;
  if (!stufe || !(stufe in FONT_SCALE_OPTIONS)) return;
  wendeSchriftgroesseAn(stufe);
  try {
    localStorage.setItem(FONT_SCALE_STORAGE_KEY, stufe);
  } catch {}
}

export function schriftgroesseZuruecksetzen() {
  try {
    localStorage.removeItem(FONT_SCALE_STORAGE_KEY);
  } catch {}
  Object.assign(THEME.fontSize, BASE_FONT_SIZES);
}

export default THEME;
